import { normalizeTimezone } from '@/lib/surveys/timezone-config'
/**
 * Feedback Desk — the pure helpers behind the `/feedback` quick filters
 * (2026-09-02).
 *
 * A WINDOW is a calendar range in the viewer's local time (Today, Yesterday,
 * Last 7 days, Last 30 days, All time). A LENS says which event the window
 * applies to (resolved / raised / needs verification / reopened / still open)
 * and therefore which status set and which date column the API filters on.
 * The five lens definitions mirror `FeedbackRepository.getDigest` exactly so
 * the chip counts always agree with the rows the list returns.
 */
import type {
  FeedbackLensKey,
  FeedbackListQuery,
  FeedbackTicket,
  FeedbackWindowKey,
} from '@/lib/types/feedback'

/**
 * The window + lens the desk opens on when the URL says nothing — for EVERY
 * view, not just the List (feedback #701).
 *
 * `all` + `raised` is the only pair `lensToQuery` compacts to `{}`, i.e. the
 * literal "no pre-applied filter" the reporter asked for. Every other lens
 * pins a status set or an EPOCH_ISO floor, and `resolved` additionally needs
 * a resolution event inside a single past calendar day — structurally empty
 * on any day the team closed nothing, which is most days and every Monday.
 *
 * This was HALF fixed on 2026-09-17 (cd48c19f) as a `view === 'list'`
 * carve-out. The landing view is the DIGEST — `parseView` returns 'digest'
 * for a bare URL — so the carve-out could never fire on first load and the
 * desk still opened on an empty page. `all` also un-narrows the BOARD, which
 * the lens never touched: the board is bounded by `boardWindow`
 * (updatedFrom/updatedTo) and is unbounded only when the window is `all`.
 *
 * The daily-review landing is now a bookmark
 * (`?window=yesterday&lens=resolved`), not the front door.
 */
export const DEFAULT_WINDOW: FeedbackWindowKey = 'all'
export const DEFAULT_LENS: FeedbackLensKey = 'raised'

/**
 * The defaults, as the one call both desks make. No `view` parameter on
 * purpose: a parameter that decides nothing is exactly how the list-only
 * carve-out hid in plain sight.
 */
export function deskDefaults(): {
  window: FeedbackWindowKey
  lens: FeedbackLensKey
} {
  return { window: DEFAULT_WINDOW, lens: DEFAULT_LENS }
}

export const WINDOWS: ReadonlyArray<{ key: FeedbackWindowKey; label: string }> = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: 'all', label: 'All time' },
]

export const LENSES: ReadonlyArray<{
  key: FeedbackLensKey
  label: string
  /** One line under the chips saying what the list is showing. */
  describe: (windowLabel: string) => string
}> = [
  {
    key: 'resolved',
    label: 'Resolved',
    describe: (w) => `tickets resolved, closed or marked won’t fix ${w}`,
  },
  {
    key: 'raised',
    label: 'Raised',
    describe: (w) => `tickets submitted ${w}`,
  },
  {
    key: 'verify',
    label: 'Needs verification',
    describe: (w) => `fixes resolved ${w} that the reporter has not confirmed yet`,
  },
  {
    key: 'reopened',
    label: 'Reopened',
    describe: (w) => `tickets the reporter sent back ${w}`,
  },
  {
    key: 'open',
    label: 'Still open',
    describe: (w) => `tickets submitted ${w} that are still open or in progress`,
  },
]

/**
 * The colour each lens owns, for the quick-filter chips (2026-09-22, #702).
 *
 * Before this, both chip arms were achromatic: `bg-primary` is
 * oklch(0.205 0 0) in light and `bg-background` is oklch(0.145 0 0) in dark,
 * so the SELECTED chip was a black pill in light mode (and `resolved` was the
 * landing lens) and every UNSELECTED chip was a black pill in dark mode. A
 * chip that names a state is painted with that state's colour instead.
 *
 * `on` is a solid fill behind WHITE text and needs 4.5:1 — measured against
 * the palette actually installed here (tailwindcss 4.x), NOT the v3 hexes.
 * `off` is the tint treatment the verification badge already ships, the one
 * pattern in this module proven to read on both grounds.
 *
 * NOT the digest strip's colours: its dots are 500-weight to match the two
 * lifecycle timelines and the board accents, and its `--series-*` vars are a
 * chart palette scoped to that component. A 2px dot carries no text and has
 * no contrast floor; a chip does. The two are allowed to differ in weight.
 */
export const LENS_TONE: Record<FeedbackLensKey, { on: string; off: string }> = {
  resolved: {
    on: 'border-green-700 bg-green-700 text-white',
    off: 'border-green-500/40 bg-green-500/10 text-green-800 hover:bg-green-500/20 dark:text-green-300',
  },
  raised: {
    on: 'border-blue-600 bg-blue-600 text-white',
    off: 'border-blue-500/40 bg-blue-500/10 text-blue-800 hover:bg-blue-500/20 dark:text-blue-300',
  },
  verify: {
    // orange-700; orange-500 is 2.89:1 behind white and orange-600 only 3.59:1.
    on: 'border-orange-700 bg-orange-700 text-white',
    off: 'border-orange-500/40 bg-orange-500/10 text-orange-800 hover:bg-orange-500/20 dark:text-orange-300',
  },
  reopened: {
    on: 'border-purple-600 bg-purple-600 text-white',
    off: 'border-purple-500/40 bg-purple-500/10 text-purple-800 hover:bg-purple-500/20 dark:text-purple-300',
  },
  open: {
    // Neutral on purpose: blue would collide with Raised, green with Resolved.
    on: 'border-slate-600 bg-slate-600 text-white',
    off: 'border-slate-500/40 bg-slate-500/10 text-slate-800 hover:bg-slate-500/20 dark:text-slate-300',
  },
}

export const CLOSED_STATUSES = ['Resolved', 'Closed', 'Wont Fix'] as const
export const OPEN_STATUSES = ['Open', 'In Progress'] as const

export function isClosedStatus(status: string | null | undefined): boolean {
  return (
    typeof status === 'string' &&
    (CLOSED_STATUSES as readonly string[]).includes(status)
  )
}

export function isWindowKey(v: string | null | undefined): v is FeedbackWindowKey {
  return WINDOWS.some((w) => w.key === v)
}

export function isLensKey(v: string | null | undefined): v is FeedbackLensKey {
  return LENSES.some((l) => l.key === v)
}

export function startOfDay(d: Date): Date {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

export interface WindowRange {
  /** Inclusive start (local midnight). */
  from?: Date
  /** EXCLUSIVE end (local midnight of the day after the last day). */
  to?: Date
}

export function windowRange(key: FeedbackWindowKey, now: Date = new Date()): WindowRange {
  const d0 = startOfDay(now)
  switch (key) {
    case 'today':
      return { from: d0, to: addDays(d0, 1) }
    case 'yesterday':
      return { from: addDays(d0, -1), to: d0 }
    case '7d':
      return { from: addDays(d0, -6), to: addDays(d0, 1) }
    case '30d':
      return { from: addDays(d0, -29), to: addDays(d0, 1) }
    case 'all':
    default:
      return {}
  }
}

/** The digest endpoint takes an exclusive `to`; pass the range through as-is. */
export function windowToDigestParams(range: WindowRange): { from?: string; to?: string } {
  return {
    from: range.from ? range.from.toISOString() : undefined,
    to: range.to ? range.to.toISOString() : undefined,
  }
}

/**
 * The list filters are `gte(col, from)` + `lte(col, to)` — an INCLUSIVE
 * upper bound — so the exclusive window end becomes "one millisecond before".
 */
function inclusiveTo(range: WindowRange): string | undefined {
  return range.to ? new Date(range.to.getTime() - 1).toISOString() : undefined
}

function fromIso(range: WindowRange): string | undefined {
  return range.from ? range.from.toISOString() : undefined
}

/**
 * The lower bound an all-time window still sends for the DATE lenses: the
 * digest counts `resolved_at IS NOT NULL` / `last_reopened_at IS NOT NULL`,
 * and `gte(col, epoch)` is how the list says the same thing. Without it an
 * all-time "Reopened" lens sent no filter and listed every ticket.
 */
export const EPOCH_ISO = '1970-01-01T00:00:00.000Z'

/** Drop undefined keys so a spread never overwrites a real value with nothing. */
function compact<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== undefined),
  ) as Partial<T>
}

/** Which list-filter params a lens sends for a window. Mirrors getDigest. */
export function lensToQuery(
  lens: FeedbackLensKey,
  range: WindowRange,
): Partial<FeedbackListQuery> {
  const from = fromIso(range)
  const to = inclusiveTo(range)
  switch (lens) {
    case 'resolved':
      return compact({
        status: [...CLOSED_STATUSES],
        resolvedFrom: from ?? EPOCH_ISO,
        resolvedTo: to,
      })
    case 'raised':
      return compact({ createdFrom: from, createdTo: to })
    case 'verify':
      return compact({
        status: ['Resolved'],
        verification: 'awaiting' as const,
        resolvedFrom: from ?? EPOCH_ISO,
        resolvedTo: to,
      })
    case 'reopened':
      return compact({ reopenedFrom: from ?? EPOCH_ISO, reopenedTo: to })
    case 'open':
    default:
      return compact({
        status: [...OPEN_STATUSES],
        createdFrom: from,
        createdTo: to,
      })
  }
}

/**
 * Intersect two inclusive ISO ranges (a calendar picker and a lens window):
 * the later `from`, the earlier `to`. Either side may be open-ended.
 */
export function tightenRange(
  a: { from?: string; to?: string },
  b: { from?: string; to?: string },
): { from?: string; to?: string } {
  const from = [a.from, b.from].filter((x): x is string => !!x).sort().at(-1)
  const to = [a.to, b.to].filter((x): x is string => !!x).sort().at(0)
  return { from, to }
}

export type LensDateField = 'resolvedAt' | 'createdAt' | 'lastReopenedAt'

/** The ticket date the digest groups by for a lens. */
export function lensDateField(lens: FeedbackLensKey): LensDateField {
  switch (lens) {
    case 'resolved':
    case 'verify':
      return 'resolvedAt'
    case 'reopened':
      return 'lastReopenedAt'
    case 'raised':
    case 'open':
    default:
      return 'createdAt'
  }
}

/** Server sort that matches the grouping, so pages arrive in day order. */
export function lensSortBy(lens: FeedbackLensKey): FeedbackListQuery['sortBy'] {
  switch (lens) {
    case 'resolved':
    case 'verify':
      return 'resolvedAt'
    case 'reopened':
      return 'lastReopenedAt'
    default:
      return 'createdAt'
  }
}

export function localDayKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export interface DayGroup {
  dayKey: string
  date: Date
  tickets: FeedbackTicket[]
}

/**
 * Group tickets by the local calendar day of `field`, newest day first,
 * newest ticket first within a day. A ticket without that date (a data gap)
 * falls back to its submission date rather than vanishing.
 */
export function groupTicketsByDay(
  tickets: FeedbackTicket[],
  field: LensDateField,
): DayGroup[] {
  const groups = new Map<string, DayGroup>()
  const stamp = (t: FeedbackTicket): Date =>
    new Date((t[field] as string | null) ?? t.createdAt)
  for (const t of [...tickets].sort((a, b) => stamp(b).getTime() - stamp(a).getTime())) {
    const date = startOfDay(stamp(t))
    const key = localDayKey(date)
    const g = groups.get(key) ?? { dayKey: key, date, tickets: [] }
    g.tickets.push(t)
    groups.set(key, g)
  }
  return [...groups.values()].sort((a, b) => b.date.getTime() - a.date.getTime())
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

/** "Today · Wednesday 2 September", "Yesterday · Tuesday 1 September", "Friday, 28 August". */
export function formatDayHeading(date: Date, now: Date = new Date()): string {
  const d0 = startOfDay(now)
  const long = `${DAY_NAMES[date.getDay()]} ${date.getDate()} ${MONTH_NAMES[date.getMonth()]}`
  if (date.getTime() === d0.getTime()) return `Today · ${long}`
  if (date.getTime() === addDays(d0, -1).getTime()) return `Yesterday · ${long}`
  const year = date.getFullYear() !== now.getFullYear() ? ` ${date.getFullYear()}` : ''
  return `${DAY_NAMES[date.getDay()]}, ${date.getDate()} ${MONTH_NAMES[date.getMonth()]}${year}`
}

export interface DaySummaryItem {
  label: string
  value: number
}

/** The counts shown beside a day heading, per lens. */
export function daySummary(lens: FeedbackLensKey, tickets: FeedbackTicket[]): DaySummaryItem[] {
  const count = (f: (t: FeedbackTicket) => boolean) => tickets.filter(f).length
  if (lens === 'raised' || lens === 'open') {
    return [
      { label: 'raised', value: tickets.length },
      { label: 'in progress', value: count((t) => t.status === 'In Progress') },
      { label: 'already resolved', value: count((t) => isClosedStatus(t.status)) },
    ]
  }
  if (lens === 'reopened') {
    return [{ label: 'reopened', value: tickets.length }]
  }
  const items: DaySummaryItem[] = [
    { label: 'resolved', value: tickets.length },
    { label: 'verified', value: count((t) => !!t.verifiedAt) },
    { label: 'awaiting', value: count((t) => t.status === 'Resolved' && !t.verifiedAt) },
  ]
  const wontFix = count((t) => t.status === 'Wont Fix')
  if (wontFix) items.push({ label: 'won’t fix', value: wontFix })
  return items
}

export type VerificationState =
  | { key: 'wait'; label: string }
  | { key: 'ok'; label: string }
  | { key: 're'; label: string }

/** The verification pill for a ticket, or null when it does not apply. */
export function verificationOf(t: FeedbackTicket): VerificationState | null {
  if (t.lastReopenedAt && !isClosedStatus(t.status)) {
    return {
      key: 're',
      label: t.reopenCount > 1 ? `Reopened ×${t.reopenCount}` : 'Reopened',
    }
  }
  if (t.status === 'Resolved' && t.verifiedAt) {
    return {
      key: 'ok',
      label: t.verifiedByName ? `Verified by ${t.verifiedByName}` : 'Verified',
    }
  }
  if (t.status === 'Resolved') return { key: 'wait', label: 'Awaiting verification' }
  return null
}

/** Browser timezone for the digest series; falls back to UTC in odd runtimes. */
export function viewerTimeZone(): string {
  try {
    // ICU reports legacy ids as canonical (Chrome: `Asia/Calcutta`), and
    // PostgreSQL 17 on Ubuntu 24.04 has no legacy links — the API guards
    // this too, but send the current IANA name so the URL and the query
    // carry the same zone the server buckets in.
    return normalizeTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
  } catch {
    return 'UTC'
  }
}

/**
 * The next wider window, for the digest's empty state: "nothing yesterday —
 * try last 7 days". `all` has nothing wider, so the hint falls back to the
 * chips. Today and yesterday both widen to the week.
 */
export function nextWiderWindow(
  key: FeedbackWindowKey,
): { key: FeedbackWindowKey; label: string } | null {
  const order: FeedbackWindowKey[] = ['today', 'yesterday', '7d', '30d', 'all']
  const idx = order.indexOf(key)
  const next = key === 'today' ? '7d' : idx >= 0 ? order[idx + 1] : undefined
  if (!next) return null
  const def = WINDOWS.find((w) => w.key === next)
  return def ? { key: def.key, label: def.label } : null
}

/**
 * URL params for picking a company on the admin desk. Drops the workspace
 * AND the submitter filter: the submitter facets are per scope, so a
 * submitter from the previous company is one nothing on screen offers, and
 * the desk showed "no feedback" for a filter it did not display
 * (2026-09-10 review).
 */
export function companyScopeParams(company: string): {
  company: string
  workspace: undefined
  submittedById: undefined
  page: '1'
} {
  return { company, workspace: undefined, submittedById: undefined, page: '1' }
}

/**
 * The row the detail drawer should render: the list's CURRENT copy of the
 * selected ticket, not the snapshot captured at click time. After Verify the
 * list refetches, and a stale snapshot kept the Verify button on a ticket
 * that was already verified (2026-09-10 review).
 */
export function freshestTicket<T extends { id: string }>(
  selected: T | null,
  tickets: ReadonlyArray<T>,
): T | null {
  if (!selected) return null
  return tickets.find((t) => t.id === selected.id) ?? selected
}
